<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue';
import { createBootstrapModal } from '../blog/utils/bootstrapModal.js';
import CardPrintLink from './CardPrintLink.vue';

const props = defineProps({ card: { type: Object, default: null } });
const emit = defineEmits(['closed', 'error']);
const modalElement = ref(null);
const titleId = `${useId()}-card-detail-title`;
let controller;
let unmounting = false;
let failed = false;

function reportFailure() {
    if (unmounting || failed) return;
    failed = true;
    emit('error');
}

async function synchronize() {
    try {
        await nextTick();
        if (!controller || unmounting || failed) return;
        await (props.card ? controller.show() : controller.hide());
    } catch {
        reportFailure();
    }
}

function close() {
    if (controller && !failed) void controller.hide().catch(reportFailure);
}

onMounted(() => {
    try {
        controller = createBootstrapModal(modalElement.value, {
            onHidden: () => { if (!unmounting && !failed) emit('closed'); }
        });
        void synchronize();
    } catch {
        reportFailure();
    }
});

watch(() => props.card, synchronize);
onBeforeUnmount(() => {
    unmounting = true;
    // The shared adapter finishes Bootstrap transitions before disposing. Vue
    // cannot await an unmount hook, so also consume a possible disposal rejection.
    if (controller) void controller.dispose().catch(() => {});
});
</script>

<template>
    <div ref="modalElement" class="modal fade" tabindex="-1" :aria-labelledby="titleId" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 :id="titleId" class="modal-title fs-5 text-break">{{ card?.title ?? 'Card details' }}</h2>
                    <button type="button" class="btn-close flex-shrink-0" aria-label="Close card details" @click="close"></button>
                </div>
                <div v-if="card" class="modal-body text-break">
                    <img v-if="card.imageUrl" :src="card.imageUrl" :alt="card.imageAlt"
                         class="img-fluid rounded mb-3 w-100 detail-image" decoding="async">
                    <!-- Only server-sanitized HTML is accepted by this component. -->
                    <div v-if="card.bodyHtml" class="card-rich-content" v-html="card.bodyHtml"></div>
                    <p v-else>{{ card.summary }}</p>
                    <div v-if="card.footerHtml" class="card-rich-content border-top pt-3 mt-3" v-html="card.footerHtml"></div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" @click="close">Close</button>
                    <CardPrintLink :url="card?.printUrl" />
                    <a v-if="card?.linkUrl" :href="card.linkUrl" class="btn btn-primary">
                        {{ card.linkLabel }}<i class="bi bi-arrow-up-right ms-1" aria-hidden="true"></i>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.detail-image {
    max-height: min(40vh, 24rem);
    object-fit: contain;
}

.card-rich-content :deep(img) {
    max-width: 100%;
    height: auto;
}
</style>
